/** The delivery-log search namespaces wired to their suggestion fetchers.
 *
 * Every namespace is a direct column on the Event_Delivery_Log row (the model
 * has no FKs), so there are no `allAny` namespaces and no scope modifier —
 * see EventDeliveryLogFilterNode in the OpenAPI spec. The set mirrors the
 * Django admin's search_fields + list_filter for the model.
 *
 * Operators per namespace are a SUBSET of the spec's
 * `x-namespace-operators` (contract-tested). `exact` is deliberately omitted
 * wherever `iexact` exists (tenant/user/departure) — the DSL serialises both
 * to `=`, which re-parses to `iexact`, so an `exact` chip couldn't round-trip
 * through the URL; ID pinning goes through the backend's all-digits `iexact`
 * fallback instead (same rule as orders/bookings). `tour`/`participant` are
 * exact-only (integer ids), which round-trips fine — `=` resolves to `exact`
 * when `iexact` is absent.
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { fetchEventSuggestions, makeSuggestionFetcher } from '@/api/eventDeliveryLogs';

import { SANDBOX_STATUS_LABEL } from './deliveryStatus';

/** Event_Delivery_Log.STATUSES labels — a small fixed enum, filtered locally
 *  (no API call). Contract-tested against the spec's `x-status-labels`. */
export const DELIVERY_STATUS_LABELS = [
  'Emitted',
  'No notification matched',
  'Rendering',
  'Scheduled for sending',
  'Sent',
  'Error',
  'Partially sent',
  'Not sent',
  'Sent with errors',
  'Orphaned (crash recovery)',
  'Orphaned and recovered',
  'Orphaned and unrecoverable',
  'Orphaned and empty',
  // A sandbox's (13–15; never on a production tenant) — the words every surface uses.
  SANDBOX_STATUS_LABEL.sentAllowlisted,
  SANDBOX_STATUS_LABEL.partiallySentAllowlisted,
  SANDBOX_STATUS_LABEL.captured,
];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildDeliveryLogNamespaces(): NamespaceConfigMap {
  return {
    event: {
      label: 'event',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchEventSuggestions,
      help: 'Event code (e.g. booking.created).',
      examples: ['event=booking.created', 'event=~tour_participant'],
    },
    log: {
      label: 'log',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Event log id — shared by all redeliveries of the same event.',
      examples: ['log=a1b2c3', 'log=~a1b2'],
    },
    tag: {
      label: 'tag',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Log tag — redelivery runs stamp their tag here (cli[…]/console[…]).',
      examples: ['tag=~console[', 'tag="cli[abc123]"'],
    },
    series: {
      label: 'series',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Error series ID — groups related problem deliveries.',
      examples: ['series=INC-42', 'series=~abc'],
    },
    has_series: {
      label: 'has_series',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Whether an error series ID is set (yes/no).',
      examples: ['has_series=yes', 'has_series=no'],
    },
    status: {
      label: 'status',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(DELIVERY_STATUS_LABELS),
      help: 'Delivery status, by label or raw integer value.',
      examples: ['status=error', 'status="sent with errors"'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id.',
      examples: ['tenant=curatedplanet', 'tenant=42'],
    },
    email: {
      label: 'email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Recipient (guest) email recorded on the delivery.',
      examples: ['email=jane@example.com', 'email=~@gmail.com'],
    },
    user: {
      label: 'user',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Recipient by email; an all-digits value also matches the user id.',
      examples: ['user=jane@example.com', 'user=12345'],
    },
    tour: {
      label: 'tour',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Tour id recorded on the delivery.',
      examples: ['tour=2041'],
    },
    notification: {
      label: 'notification',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Notification id that produced the delivery (the notifications grid links here).',
      examples: ['notification=314'],
    },
    participant: {
      label: 'participant',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Participant (booking membership) id recorded on the delivery.',
      examples: ['participant=88123'],
    },
    departure: {
      label: 'departure',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Departure time label recorded on the delivery.',
      examples: ['departure=09:30'],
    },
    order: {
      label: 'order',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Order reference from the booking system.',
      examples: ['order=ORD-100', 'order=~ORD-1'],
    },
    booking: {
      label: 'booking',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Booking ID from the booking system (custom id or alt id).',
      examples: ['booking=BK-1', 'booking=~BK'],
    },
    supplier: {
      label: 'supplier',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Supplier reference.',
      examples: ['supplier=SUP-1', 'supplier=~SUP'],
    },
    reseller: {
      label: 'reseller',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Reseller reference.',
      examples: ['reseller=RES-1', 'reseller=~RES'],
    },
    request: {
      label: 'request',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'HTTP request id that emitted the event (cross-service correlation).',
      examples: ['request=~abcd1234'],
    },
    // created/finalized: exact = a whole calendar day; range bounds are
    // date-times (naive, read as UTC by the backend — bare dates still parse
    // for hand-typed/legacy queries). `created` is the partition key — keeping
    // a bounded created window on the search is what keeps queries fast.
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the event was emitted, UTC. `=` matches a whole day; a range takes date-time bounds. Keep a bounded window for fast searches.',
      examples: ['created=2026-07-20', 'created="2026-07-14 09:00".."2026-07-21 18:00"'],
    },
    finalized: {
      label: 'finalized',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the delivery finished processing, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['finalized=2026-07-20', 'finalized="2026-07-14 09:00".."2026-07-21 18:00"'],
    },
  };
}
